import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Box, Typography, Grid, Button, Link, InputBase, Paper } from '@mui/material';
import { Search, ArrowLeft, Home, BookOpen, Terminal, Cpu } from 'lucide-react';
import ErrorLayout from '../components/ErrorLayout';

const LATEST_ARTICLES = [
  {
    title: "Best Open-Source AI Agent Frameworks in 2026: A Complete Comparison Guide",
    slug: "best-open-source-ai-agent-frameworks-2026",
    category: "AI Engineering"
  },
  {
    title: "AI Agent Memory Explained: Short-Term vs Long-Term Memory",
    slug: "ai-agent-memory-short-term-vs-long-term-memory",
    category: "AI Agents"
  },
  {
    title: "How to Build a Micro-SaaS in a Weekend Using AI Tools",
    slug: "build-micro-saas-weekend-ai-tools-2026",
    category: "SaaS Dev"
  }
];

const POPULAR_TOPICS = [
  "AI Agents",
  "Software Testing",
  "Python",
  "Data Engineering",
  "DevOps",
  "Cloud",
  "Artificial Intelligence"
];

export default function Custom404() {
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/blogs?search=${encodeURIComponent(searchQuery)}`;
    }
  };

  const goBack = () => {
    if (typeof window !== 'undefined') {
      window.history.back();
    }
  };

  return (
    <ErrorLayout errorCode="404">
      <Grid container spacing={6} alignItems="center" sx={{ px: { xs: 2, md: 4 }, width: '100%', m: 0 }}>
        
        {/* Left column: Text info and interactions */}
        <Grid item xs={12} lg={7} sx={{ display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'left', p: '0 !important' }}>
          
          {/* Status Tag */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4 }}
            style={{ width: 'fit-content' }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'rgba(6, 182, 212, 0.1)',
                border: '1px solid rgba(6, 182, 212, 0.2)',
                color: '#22d3ee',
                fontSize: '11px',
                fontFamily: 'monospace',
                fontWeight: 600,
                px: 2,
                py: 0.75,
                borderRadius: '9999px',
              }}
            >
              <Terminal size={14} />
              <span>HTTP STATUS_CODE: 404_NOT_FOUND</span>
            </Box>
          </motion.div>

          <Box>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 800,
                color: '#ffffff',
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                lineHeight: 1.2,
                mb: 2,
              }}
            >
              Looks like this page wandered into another branch.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#94a3b8',
                fontSize: { xs: '14px', sm: '16px' },
                maxWidth: '500px',
                lineHeight: 1.6,
              }}
            >
              The article or page you're looking for may have been moved, renamed, or no longer exists. Let's get you back on track.
            </Typography>
          </Box>

          {/* Search form */}
          <Paper
            component="form"
            onSubmit={handleSearchSubmit}
            sx={{
              p: '4px 8px',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              maxWidth: '440px',
              bgcolor: 'rgba(13, 18, 28, 0.7)',
              border: '1px solid #1e293b',
              borderRadius: '12px',
              boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)',
              '&:focus-within': {
                borderColor: '#06b6d4',
              }
            }}
          >
            <Box sx={{ p: '8px', color: '#64748b', display: 'flex', alignItems: 'center' }}>
              <Search size={18} />
            </Box>
            <InputBase
              placeholder="Search technical articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                ml: 1,
                flex: 1,
                color: '#f1f5f9',
                fontSize: '13px',
                fontFamily: 'monospace',
                input: {
                  '&::placeholder': {
                    color: '#475569',
                    opacity: 1,
                  }
                }
              }}
            />
            <Button
              type="submit"
              variant="contained"
              sx={{
                bgcolor: '#0891b2',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 600,
                px: 2.5,
                py: 1,
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#06b6d4',
                  boxShadow: 'none',
                }
              }}
            >
              Search
            </Button>
          </Paper>

          {/* Buttons */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
            <Button
              onClick={goBack}
              startIcon={<ArrowLeft size={16} />}
              sx={{
                border: '1px solid #1e293b',
                color: '#cbd5e1',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '13px',
                px: 2.5,
                py: 1,
                borderRadius: '10px',
                '&:hover': {
                  bgcolor: 'rgba(30, 41, 59, 0.4)',
                  borderColor: '#334155',
                }
              }}
            >
              Back
            </Button>
            <Button
              href="/"
              startIcon={<Home size={16} />}
              sx={{
                border: '1px solid #1e293b',
                color: '#cbd5e1',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '13px',
                px: 2.5,
                py: 1,
                borderRadius: '10px',
                '&:hover': {
                  bgcolor: 'rgba(30, 41, 59, 0.4)',
                  borderColor: '#334155',
                }
              }}
            >
              Home
            </Button>
            <Button
              href="/blogs"
              startIcon={<BookOpen size={16} />}
              sx={{
                bgcolor: '#0891b2',
                color: '#ffffff',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '13px',
                px: 2.5,
                py: 1.1,
                borderRadius: '10px',
                boxShadow: '0 4px 12px rgba(8, 145, 178, 0.25)',
                '&:hover': {
                  bgcolor: '#06b6d4',
                  boxShadow: '0 4px 16px rgba(8, 145, 178, 0.4)',
                }
              }}
            >
              Browse Articles
            </Button>
          </Box>

          {/* Exception block */}
          <Box
            sx={{
              width: '100%',
              maxWidth: '500px',
              border: '1px solid rgba(30, 41, 59, 0.8)',
              bgcolor: 'rgba(13, 18, 28, 0.4)',
              borderRadius: '12px',
              p: 2.5,
              fontFamily: 'monospace',
              fontSize: '11px',
              color: '#f87171',
              boxShadow: 'inset 0 2px 4px 0 rgba(0,0,0,0.06)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #0f172a', pb: 1, mb: 1.5, color: '#475569' }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'rgba(239, 68, 68, 0.6)' }} />
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'rgba(234, 179, 8, 0.6)' }} />
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'rgba(34, 197, 94, 0.6)' }} />
              <Box component="span" sx={{ pl: 1, fontSize: '10px' }}>exception_logger.log</Box>
            </Box>
            <pre style={{ margin: 0, overflowX: 'auto', leadingHeight: 1.5 }}>
{`Error:
PageNotFoundException:
Expected page found = false
  at RouteResolver.resolve (router.ts:314)
  at CoreTechTalksApp.run (main.ts:89)`}
            </pre>
          </Box>
        </Grid>

        {/* Right column: Interactive searching robot */}
        <Grid item xs={12} lg={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px', position: 'relative', p: '0 !important' }}>
          <Box sx={{ position: 'relative', width: '100%', maxWidth: '340px', aspectRatio: '1/1', display: 'flex', alignItems: 'center', justifySelf: 'center', justifyContent: 'center' }}>
            
            {/* Ambient glow */}
            <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(6, 182, 212, 0.08)', borderRadius: '50%', filter: 'blur(80px)' }} />

            {/* Floating code snippet 1 */}
            <Box
              component={motion.div}
              animate={{ y: [-8, 8, -8], rotate: [-2, 2, -2] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              sx={{
                position: 'absolute',
                top: '-16px',
                left: '-16px',
                zIndex: 20,
                bgcolor: 'rgba(13, 18, 28, 0.85)',
                border: '1px solid #1e293b',
                borderRadius: '8px',
                p: 1.5,
                boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '9px', color: '#22d3ee' }}>
{`const match = slug => {
  return blog.find(match);
}`}
              </pre>
            </Box>

            {/* Floating code snippet 2 */}
            <Box
              component={motion.div}
              animate={{ y: [10, -10, 10], rotate: [3, -3, 3] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              sx={{
                position: 'absolute',
                bottom: '-24px',
                right: '-8px',
                zIndex: 20,
                bgcolor: 'rgba(13, 18, 28, 0.85)',
                border: '1px solid #1e293b',
                borderRadius: '8px',
                p: 1.5,
                boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '9px', color: '#34d399' }}>
{`import { AgentHarness } from 'harness';
const runner = new AgentHarness();`}
              </pre>
            </Box>

            {/* Main Robot Block */}
            <Box
              sx={{
                width: '192px',
                height: '192px',
                border: '1px solid rgba(30, 41, 59, 0.8)',
                background: 'linear-gradient(to bottom, rgba(15, 23, 42, 0.6), rgba(2, 6, 23, 0.9))',
                borderRadius: '24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
                backdropFilter: 'blur(16px)',
                position: 'relative',
              }}
            >
              {/* Antenna */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '-12px',
                  width: '6px',
                  height: '16px',
                  bgcolor: '#06b6d4',
                  borderRadius: '9999px',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-8px',
                    left: '-6px',
                    width: '18px',
                    height: '18px',
                    bgcolor: '#22d3ee',
                    borderRadius: '50%',
                    filter: 'blur(4px)',
                  }}
                />
              </Box>

              {/* Face screen */}
              <Box
                sx={{
                  width: '144px',
                  height: '112px',
                  border: '1px solid #1e293b',
                  bgcolor: 'rgba(13, 18, 28, 0.8)',
                  borderRadius: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1.5,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Eyes */}
                <Box sx={{ display: 'flex', gap: 3.5 }}>
                  <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: '#083344', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    <Box
                      component={motion.div}
                      animate={{ scale: [1, 1.4, 1] }}
                      transition={{ duration: 3, repeat: Infinity }}
                      sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#22d3ee', filter: 'blur(0.5px)' }}
                    />
                  </Box>
                  <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: '#083344', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    <Box
                      component={motion.div}
                      animate={{ scale: [1, 1.4, 1] }}
                      transition={{ duration: 3, repeat: Infinity }}
                      sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#22d3ee', filter: 'blur(0.5px)' }}
                    />
                  </Box>
                </Box>

                {/* Mouth indicator */}
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'rgba(6, 182, 212, 0.4)' }} />
                  <Box sx={{ width: 18, height: 6, borderRadius: '3px', bgcolor: '#06b6d4' }} />
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'rgba(6, 182, 212, 0.4)' }} />
                </Box>

                {/* Scanner laser beam line */}
                <Box
                  component={motion.div}
                  animate={{ y: [-30, 30, -30] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                  sx={{
                    position: 'absolute',
                    left: 16,
                    right: 16,
                    height: '2px',
                    bgcolor: '#22d3ee',
                    boxShadow: '0 0 10px #22d3ee',
                    zIndex: 10,
                  }}
                />
              </Box>

              {/* Status footer text */}
              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, fontSize: '10px', color: '#22d3ee', fontFamily: 'monospace' }}>
                <Cpu size={12} className="animate-spin" />
                <span>SEARCHING_BRANCH...</span>
              </Box>
            </Box>

          </Box>
        </Grid>
      </Grid>

      {/* Grid footer for topics and recent posts */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '1000px',
          mt: 10,
          pt: 6,
          borderTop: '1px solid rgba(15, 23, 42, 0.6)',
          textAlign: 'left',
          px: 2,
        }}
      >
        <Grid container spacing={4}>
          {/* Topics List */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontFamily: 'monospace',
                mb: 2.5,
              }}
            >
              Popular Topics
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {POPULAR_TOPICS.map((topic) => (
                <Link
                  key={topic}
                  href={`/blogs?search=${encodeURIComponent(topic)}`}
                  sx={{
                    bgcolor: 'rgba(15, 23, 42, 0.4)',
                    border: '1px solid rgba(30, 41, 59, 0.6)',
                    color: '#94a3b8',
                    fontSize: '12px',
                    fontWeight: 500,
                    px: 1.75,
                    py: 0.75,
                    borderRadius: '8px',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(6, 182, 212, 0.05)',
                      borderColor: 'rgba(6, 182, 212, 0.4)',
                      color: '#22d3ee',
                    }
                  }}
                >
                  {topic}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Articles list */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontFamily: 'monospace',
                mb: 2.5,
              }}
            >
              Latest Articles
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {LATEST_ARTICLES.map((article) => (
                <Link
                  key={article.slug}
                  href={`/blogs/${article.slug}`}
                  sx={{
                    display: 'block',
                    p: 2,
                    bgcolor: 'rgba(15, 23, 42, 0.3)',
                    border: '1px solid rgba(30, 41, 59, 0.4)',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(13, 18, 28, 0.4)',
                      borderColor: 'rgba(30, 41, 59, 0.8)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Box component="span" sx={{ fontSize: '10px', color: '#475569', fontFamily: 'monospace', textTransform: 'uppercase' }}>
                      {article.category}
                    </Box>
                    <Box component="span" sx={{ fontSize: '11px', color: '#06b6d4', fontWeight: 600 }}>
                      Read &rarr;
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#e2e8f0', fontWeight: 600, fontSize: '13px' }}>
                    {article.title}
                  </Typography>
                </Link>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </ErrorLayout>
  );
}
